/**
 * Telephony: the Twilio account and the phone numbers on it.
 *
 * THREE SECTIONS, THREE POSTURES. Credentials are ADMIN-ONLY to change and the
 * auth token is write-only — the server stores it, verifies it against Twilio,
 * and never returns it; every other role sees the SID and the fact that a token
 * exists. The inventory is readable by everyone and writable by any writer, and
 * every action on it is IMMEDIATE — assign, fix, release each fire their own
 * request and update the row from the response; there is no draft and no Save,
 * unlike the agent form. Buying is a writer's action behind a two-step confirm,
 * because it spends real money on the Twilio account.
 *
 * TWO OF THE ACTIONS HERE ARE NOT UNDOABLE FROM THIS PANEL: a purchase charges
 * the account the moment Twilio accepts it, and a release hands the number back
 * to the carrier pool forever. Both get the arm-then-confirm idiom from the
 * revoke panels rather than a modal — two clicks, the second visibly scarier.
 *
 * SYNC RUNS ONCE ON MOUNT for a writer with credentials configured, so the
 * table opens showing what Twilio actually holds rather than what the panel
 * last saw — a number released from the Twilio console, or bought there, shows
 * up without anyone knowing to press the button. The button remains for the
 * mid-session case.
 */
import { useEffect, useRef, useState } from 'react'
import {
  ApiError,
  agents as agentsApi,
  telephony,
  type AgentConfig,
  type AvailableNumberView,
  type CredentialsView,
  type PhoneNumberView,
} from '@/api'
import { can } from '@/lib/auth'
import { telephonyMessageFor as messageFor } from '@/lib/telephony-form'
import { cn } from '@/lib/utils'
import { useAuth } from '@/components/auth-provider'
import { Link } from '@/components/app-sidebar'
import { DataTable, PageHeader, type Column } from '@/components/data-table'
import { DASH, fmtWhen } from '@/components/state-pill'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

/** The sentinel for "no agent" — Radix Select refuses an empty-string item
 *  value. Safe: real agent ids can never be the bare word `none`. */
const UNASSIGNED = 'none'

export function TelephonyPage() {
  const { user } = useAuth()
  const canWrite = can(user, 'write')
  const isAdmin = user?.role === 'admin'

  // ------------------------------------------------------------- credentials
  const [creds, setCreds] = useState<CredentialsView | null>(null)
  const [credsError, setCredsError] = useState<string | null>(null)
  /** Null = untouched, render the stored SID. The WebhookEdit testParams idiom. */
  const [sidDraft, setSidDraft] = useState<string | null>(null)
  const [tokenDraft, setTokenDraft] = useState('')
  const [credsSaving, setCredsSaving] = useState(false)
  const [credsSaved, setCredsSaved] = useState(false)
  const [credsSaveError, setCredsSaveError] = useState<string | null>(null)

  // ---------------------------------------------------------------- numbers
  const [rows, setRows] = useState<PhoneNumberView[] | null>(null)
  const [rowsError, setRowsError] = useState<string | null>(null)
  const [showReleased, setShowReleased] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [syncError, setSyncError] = useState<string | null>(null)
  /** One line for whichever per-row action failed last — the row itself has no
   *  room for a sentence. */
  const [actionError, setActionError] = useState<string | null>(null)
  const [assigning, setAssigning] = useState<string | null>(null)
  const [fixing, setFixing] = useState<string | null>(null)
  const [armedRelease, setArmedRelease] = useState<string | null>(null)
  const [releasing, setReleasing] = useState<string | null>(null)
  /** Once the mount sync has replaced the rows, the slower plain GET (fired in
   *  parallel) must not land stale rows on top of them. */
  const syncReplaced = useRef(false)
  /** The mount sync fires once per page visit, not once per creds re-save. */
  const syncFired = useRef(false)

  // The agent list, for the assign selects. Independent failure: the selects
  // fall back to showing only each row's current value.
  const [agentRows, setAgentRows] = useState<AgentConfig[] | null>(null)

  // -------------------------------------------------------------------- buy
  const [country, setCountry] = useState('US')
  const [contains, setContains] = useState('')
  const [numType, setNumType] = useState<'local' | 'tollfree'>('local')
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  /** Null = never searched; [] = a real "Twilio offered nothing" answer. */
  const [results, setResults] = useState<AvailableNumberView[] | null>(null)
  const [armedBuy, setArmedBuy] = useState<string | null>(null)
  const [buying, setBuying] = useState<string | null>(null)
  const [buyError, setBuyError] = useState<string | null>(null)

  // Three independent loads with independent failures — a credentials 403 must
  // not blank the inventory, and a dead agents list must not blank either.
  useEffect(() => {
    const ac = new AbortController()
    telephony
      .credentials(ac.signal)
      .then((view) => {
        if (ac.signal.aborted) return
        setCreds(view)
        setCredsError(null)
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setCredsError(messageFor(e))
      })
    telephony
      .numbers(ac.signal)
      .then((res) => {
        if (ac.signal.aborted || syncReplaced.current) return
        setRows(res.rows)
        setRowsError(null)
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setRowsError(messageFor(e))
      })
    agentsApi
      .list(ac.signal)
      .then((res) => {
        if (ac.signal.aborted) return
        setAgentRows(res.rows)
      })
      .catch(() => {
        /* the selects degrade to current-value-only; nothing to report */
      })
    return () => ac.abort()
  }, [])

  // ONE sync on mount, once the credentials read says there is something to
  // sync with. A timeout signal rather than the component's controller — sync
  // WRITES (it re-persists the inventory), and a write must not be cancelled
  // because the reader navigated away mid-flight.
  useEffect(() => {
    if (!canWrite || syncFired.current) return
    if (!creds || creds.accountSid === null || !creds.authTokenSet) return
    syncFired.current = true
    setSyncing(true)
    telephony
      .sync(AbortSignal.timeout(30_000))
      .then((res) => {
        syncReplaced.current = true
        setRows(res.rows)
        setRowsError(null)
        setSyncing(false)
      })
      .catch((e: unknown) => {
        setSyncing(false)
        // Credentials vanished between the read and the sync (another tab, a
        // race with the admin) — the plain list already on screen is the answer.
        if (e instanceof ApiError && e.code === 'no_credentials') return
        setSyncError(messageFor(e))
      })
  }, [creds, canWrite])

  // ----------------------------------------------------------------- actions

  const onSaveCreds = () => {
    if (credsSaving) return
    const accountSid = (sidDraft ?? creds?.accountSid ?? '').trim()
    const body: { accountSid: string; authToken?: string } = { accountSid }
    // Only when non-empty: an empty field means "keep the stored token", and
    // the server reads an absent key the same way.
    if (tokenDraft.trim() !== '') body.authToken = tokenDraft.trim()
    setCredsSaving(true)
    setCredsSaved(false)
    setCredsSaveError(null)
    telephony
      .saveCredentials(body, AbortSignal.timeout(30_000))
      .then((view) => {
        setCredsSaving(false)
        setCredsSaved(true)
        setCreds(view)
        setSidDraft(null)
        setTokenDraft('')
      })
      .catch((e: unknown) => {
        setCredsSaving(false)
        setCredsSaveError(messageFor(e))
      })
  }

  const runSync = () => {
    if (syncing) return
    setSyncing(true)
    setSyncError(null)
    telephony
      .sync(AbortSignal.timeout(30_000))
      .then((res) => {
        syncReplaced.current = true
        setSyncing(false)
        setRows(res.rows)
        setRowsError(null)
      })
      .catch((e: unknown) => {
        setSyncing(false)
        setSyncError(messageFor(e))
      })
  }

  /** Replace one row with the server's version of it. */
  const putRow = (updated: PhoneNumberView) => {
    setRows((prev) =>
      prev === null ? prev : prev.map((r) => (r.sid === updated.sid ? updated : r)),
    )
  }

  const onAssign = (sid: string, agentId: string | null) => {
    setAssigning(sid)
    setActionError(null)
    telephony
      .assign(sid, agentId, AbortSignal.timeout(20_000))
      .then((updated) => {
        setAssigning(null)
        putRow(updated)
      })
      .catch((e: unknown) => {
        setAssigning(null)
        setActionError(messageFor(e))
      })
  }

  const onFix = (sid: string) => {
    setFixing(sid)
    setActionError(null)
    telephony
      .repairWebhook(sid, AbortSignal.timeout(20_000))
      .then((updated) => {
        setFixing(null)
        putRow(updated)
      })
      .catch((e: unknown) => {
        setFixing(null)
        setActionError(messageFor(e))
      })
  }

  const onRelease = (sid: string) => {
    if (releasing !== null) return
    setReleasing(sid)
    setActionError(null)
    telephony
      .release(sid, AbortSignal.timeout(30_000))
      .then(() => {
        setReleasing(null)
        setArmedRelease(null)
        // A 204 carries no row, so the local one is marked rather than
        // refetched — a refetch that failed here would print an error about a
        // release that succeeded. The next sync is authoritative.
        setRows((prev) =>
          prev === null
            ? prev
            : prev.map((r) =>
                r.sid === sid
                  ? {
                      ...r,
                      status: 'released' as const,
                      agentId: null,
                      agentName: null,
                      webhookOk: null,
                      releasedAt: new Date().toISOString(),
                    }
                  : r,
              ),
        )
      })
      .catch((e: unknown) => {
        setReleasing(null)
        setArmedRelease(null)
        setActionError(messageFor(e))
      })
  }

  const runSearch = () => {
    if (searching) return
    const c = country.trim().toUpperCase()
    if (c.length !== 2) {
      setSearchError('Country is a 2-letter ISO code, like US or GB.')
      return
    }
    setSearching(true)
    setSearchError(null)
    setArmedBuy(null)
    telephony
      .available(
        { country: c, contains: contains.trim() || undefined, type: numType },
        AbortSignal.timeout(30_000),
      )
      .then((res) => {
        setSearching(false)
        setResults(res.rows)
      })
      .catch((e: unknown) => {
        setSearching(false)
        setSearchError(messageFor(e))
      })
  }

  const onBuy = (number: string) => {
    if (buying !== null) return
    setBuying(number)
    setBuyError(null)
    telephony
      .purchase(number, AbortSignal.timeout(45_000))
      .then((row) => {
        setBuying(null)
        setArmedBuy(null)
        setResults((prev) => (prev === null ? prev : prev.filter((r) => r.number !== number)))
        // The response row lands immediately; the refresh behind it picks up
        // anything else that changed, and its failure is not this purchase's.
        setRows((prev) =>
          prev === null ? [row] : [row, ...prev.filter((r) => r.sid !== row.sid)],
        )
        telephony
          .numbers(AbortSignal.timeout(20_000))
          .then((res) => {
            syncReplaced.current = true
            setRows(res.rows)
          })
          .catch(() => {
            /* the prepended row already tells the truth about this purchase */
          })
      })
      .catch((e: unknown) => {
        setBuying(null)
        setArmedBuy(null)
        setBuyError(messageFor(e))
      })
  }

  // ------------------------------------------------------------------- table

  const releasedCount = rows?.filter((r) => r.status === 'released').length ?? 0
  const visibleRows =
    rows === null ? null : showReleased ? rows : rows.filter((r) => r.status === 'active')
  const credsConfigured = creds !== null && creds.accountSid !== null && creds.authTokenSet

  const numberColumns: Column<PhoneNumberView>[] = [
    {
      key: 'number',
      header: 'Number',
      width: '13rem',
      cell: (r) => (
        <span className={cn('whitespace-nowrap', r.status === 'released' && 'opacity-50')}>
          <span className="mono">{r.number}</span>
          {r.status === 'released' && <span className="pill pill-plain ml-2">released</span>}
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      hideBelow: 'md',
      cell: (r) =>
        r.friendlyName === null || r.friendlyName === r.number ? (
          <span className="empty">{DASH}</span>
        ) : (
          <span
            className={cn('block truncate text-sm', r.status === 'released' && 'opacity-50')}
            title={r.friendlyName}
          >
            {r.friendlyName}
          </span>
        ),
    },
    {
      key: 'agent',
      header: 'Agent',
      width: '11rem',
      cell: (r) =>
        r.agentId === null ? (
          <span className="empty">{DASH}</span>
        ) : (
          <Link
            to={`/agents/${encodeURIComponent(r.agentId)}`}
            className={cn(
              'block truncate underline underline-offset-4',
              r.status === 'released' && 'opacity-50',
            )}
            title={r.agentName ?? r.agentId}
          >
            {r.agentName ?? r.agentId}
          </Link>
        ),
    },
    {
      key: 'webhook',
      header: 'Webhook',
      width: canWrite ? '12rem' : '9rem',
      cell: (r) =>
        r.status === 'released' || r.webhookOk === null ? (
          <span className="empty">{DASH}</span>
        ) : r.webhookOk ? (
          <span
            className="text-xs text-muted-foreground"
            title={r.voiceUrl ? `Voice URL: ${r.voiceUrl}` : undefined}
          >
            ok
          </span>
        ) : (
          <span className="flex flex-wrap items-center gap-2">
            <span
              className="text-xs text-ev-bad"
              title={
                r.voiceUrl
                  ? `Twilio holds ${r.voiceUrl}, which is not this deployment. Inbound calls to this number do not reach an agent.`
                  : 'Twilio holds no Voice URL for this number, so inbound calls reach nothing.'
              }
            >
              points elsewhere
            </span>
            {canWrite && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-6 px-2 text-xs"
                disabled={fixing === r.sid}
                title="Rewrites the number's Voice webhook on Twilio to point back at this deployment."
                onClick={() => onFix(r.sid)}
              >
                {fixing === r.sid ? 'Fixing…' : 'Fix'}
              </Button>
            )}
          </span>
        ),
    },
    ...(canWrite
      ? [
          {
            key: 'actions',
            header: 'Actions',
            width: '22rem',
            cell: (r: PhoneNumberView) =>
              r.status === 'released' ? (
                <span className="empty">{DASH}</span>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    value={r.agentId ?? UNASSIGNED}
                    onValueChange={(v) => onAssign(r.sid, v === UNASSIGNED ? null : v)}
                    disabled={assigning === r.sid}
                  >
                    <SelectTrigger size="sm" className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={UNASSIGNED}>
                        <span className="text-muted-foreground">Unassigned</span>
                      </SelectItem>
                      {/* The withCurrent rule: an assigned agent the list does
                          not know (deleted, or the list failed to load) still
                          renders, so opening this page cannot rewrite the row. */}
                      {r.agentId !== null
                        && !(agentRows?.some((a) => a.id === r.agentId) ?? false) && (
                          <SelectItem value={r.agentId}>
                            <span className="mono">{r.agentName ?? r.agentId}</span>
                          </SelectItem>
                        )}
                      {agentRows?.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {armedRelease === r.sid ? (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        className="h-7 text-xs"
                        disabled={releasing === r.sid}
                        onClick={() => onRelease(r.sid)}
                      >
                        {releasing === r.sid ? 'Releasing…' : 'Release — irreversible'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        disabled={releasing === r.sid}
                        onClick={() => setArmedRelease(null)}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      title="Hands the number back to the carrier pool at Twilio. Irreversible — asks again before doing it."
                      onClick={() => setArmedRelease(r.sid)}
                    >
                      Release
                    </Button>
                  )}
                </div>
              ),
          } satisfies Column<PhoneNumberView>,
        ]
      : []),
  ]

  const availColumns: Column<AvailableNumberView>[] = [
    {
      key: 'number',
      header: 'Number',
      width: '12rem',
      cell: (r) => <span className="mono whitespace-nowrap">{r.number}</span>,
    },
    {
      key: 'locality',
      header: 'Locality',
      cell: (r) => r.locality ?? <span className="empty">{DASH}</span>,
    },
    {
      key: 'region',
      header: 'Region',
      hideBelow: 'md',
      cell: (r) => r.region ?? <span className="empty">{DASH}</span>,
    },
    {
      key: 'country',
      header: 'Country',
      width: '6rem',
      hideBelow: 'md',
      cell: (r) => <span className="mono">{r.isoCountry}</span>,
    },
    {
      key: 'buy',
      header: '',
      width: '14rem',
      cell: (r) =>
        armedBuy === r.number ? (
          <span className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              className="h-7 text-xs"
              disabled={buying === r.number}
              onClick={() => onBuy(r.number)}
            >
              {buying === r.number ? 'Buying…' : 'Confirm — real money'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              disabled={buying === r.number}
              onClick={() => setArmedBuy(null)}
            >
              Cancel
            </Button>
          </span>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            title="Buying charges the Twilio account the moment Twilio accepts — asks again before doing it."
            onClick={() => setArmedBuy(r.number)}
          >
            Buy…
          </Button>
        ),
    },
  ]

  // ------------------------------------------------------------------ render

  return (
    <div className="min-w-0 max-w-5xl">
      <PageHeader
        title="Telephony"
        subtitle={
          <>
            The Twilio account behind inbound calls, and which agent answers each number.
            Credentials are saved once by an admin; numbers are synced from Twilio and pointed
            at agents here. Assignments apply to the NEXT inbound call — there is no Save step
            on this page.
          </>
        }
      />

      {/* ------------------------------------------------------ credentials */}
      <section className="panel">
        <h2>
          Twilio credentials
          <span className="note">
            admin-managed &middot; the auth token is write-only — stored server-side, never
            shown back
          </span>
        </h2>
        <div className="body flex flex-col gap-4">
          {credsError ? (
            <p className="err text-sm">{credsError}</p>
          ) : creds === null ? (
            <p className="loading">Loading…</p>
          ) : isAdmin ? (
            <>
              <div className="grid2">
                <div className="field">
                  <Label htmlFor="twSid">Account SID</Label>
                  <Input
                    id="twSid"
                    value={sidDraft ?? creds.accountSid ?? ''}
                    onChange={(e) => {
                      setSidDraft(e.target.value)
                      setCredsSaved(false)
                    }}
                    className="mono"
                    placeholder="AC…"
                    autoCapitalize="none"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <p className="text-xs text-muted-foreground">
                    A public identifier — it is in every Twilio console URL — so it is shown,
                    unlike the token.
                  </p>
                </div>
                <div className="field">
                  <Label htmlFor="twToken">Auth token</Label>
                  <Input
                    id="twToken"
                    type="password"
                    value={tokenDraft}
                    onChange={(e) => {
                      setTokenDraft(e.target.value)
                      setCredsSaved(false)
                    }}
                    placeholder={creds.authTokenSet ? 'configured — leave blank to keep' : 'required'}
                    autoComplete="off"
                  />
                  <p className="text-xs text-muted-foreground">
                    Verified against Twilio at save time and stored server-side. It never comes
                    back to a browser — leaving this blank keeps the stored token.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  onClick={onSaveCreds}
                  disabled={
                    credsSaving
                    || (sidDraft ?? creds.accountSid ?? '').trim() === ''
                    || (!creds.authTokenSet && tokenDraft.trim() === '')
                  }
                >
                  {credsSaving ? 'Verifying with Twilio…' : 'Save credentials'}
                </Button>
                {credsSaved && (
                  <span className="text-sm text-ev-good">Saved — Twilio accepted the pair.</span>
                )}
                {creds.updatedAt && !credsSaved && (
                  <span className="text-xs text-muted-foreground">
                    Last saved {fmtWhen(creds.updatedAt)}
                    {creds.updatedBy ? <> by {creds.updatedBy}</> : null}.
                  </span>
                )}
                {credsSaveError && <p className="err w-full text-sm">{credsSaveError}</p>}
              </div>
            </>
          ) : creds.accountSid === null ? (
            <p className="max-w-2xl text-sm text-muted-foreground">
              No Twilio credentials are configured yet, and credentials are admin-managed — an
              admin saves the Account SID and auth token here before any numbers can be synced
              or bought.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="field">
                <span className="label">account SID</span>
                <span className="mono text-sm">{creds.accountSid}</span>
              </div>
              <p className="max-w-2xl text-sm text-muted-foreground">
                {creds.authTokenSet
                  ? 'Auth token configured — its value is write-only for every role.'
                  : 'No auth token is stored yet.'}{' '}
                Credentials are admin-managed; your role can read this page but not change
                them.
                {creds.updatedAt ? (
                  <>
                    {' '}
                    Last saved {fmtWhen(creds.updatedAt)}
                    {creds.updatedBy ? <> by {creds.updatedBy}</> : null}.
                  </>
                ) : null}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ---------------------------------------------------------- numbers */}
      <section className="panel">
        <h2>
          Phone numbers
          <span className="note">
            every action here is immediate &middot; assignments answer the NEXT inbound call
          </span>
        </h2>
        <div className="body flex flex-col gap-3">
          {canWrite && (
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={runSync}
                disabled={syncing}
              >
                {syncing ? 'Syncing…' : 'Sync from Twilio'}
              </Button>
              <span className="text-xs text-muted-foreground">
                Re-pulls the account&rsquo;s numbers — picks up anything bought or released
                outside this panel. Runs once on its own when this page opens.
              </span>
              {syncError && <p className="err w-full text-sm">{syncError}</p>}
            </div>
          )}
          {rowsError && <p className="err text-sm">{rowsError}</p>}
          {actionError && <p className="err text-sm">{actionError}</p>}
          <DataTable
            columns={numberColumns}
            rows={visibleRows}
            keyOf={(r) => r.sid}
            fixed
            maxHeight="none"
            emptyTitle="No phone numbers"
            emptyBody={
              releasedCount > 0 ? (
                <>Every number on file has been released — the toggle below shows them.</>
              ) : (
                <>
                  Numbers appear after an admin saves the Twilio credentials above and the
                  inventory is synced. Numbers the account already holds arrive with a sync;
                  new ones can be bought below.
                </>
              )
            }
          />
          {releasedCount > 0 && (
            <button
              type="button"
              className="cursor-pointer self-start text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
              onClick={() => setShowReleased((v) => !v)}
            >
              {showReleased ? `hide released (${releasedCount})` : `show released (${releasedCount})`}
            </button>
          )}
        </div>
      </section>

      {/* -------------------------------------------------------------- buy */}
      <section className="panel">
        <h2>
          Buy a number
          <span className="note">
            searches Twilio&rsquo;s pool &middot; a purchase charges the Twilio account real
            money
          </span>
        </h2>
        <div className="body flex flex-col gap-3">
          {!canWrite ? (
            <p className="max-w-2xl text-sm text-muted-foreground">
              Buying a number is a write, and this account is a{' '}
              <span className="mono">{user?.role}</span> — the inventory above is open to you.
            </p>
          ) : (
            <>
              {!credsConfigured && (
                <p className="max-w-2xl text-sm text-muted-foreground">
                  Searching and buying need the Twilio credentials above saved first.
                </p>
              )}
              <div className="flex flex-wrap items-end gap-3">
                <div className="field">
                  <Label htmlFor="buyCountry">Country</Label>
                  <Input
                    id="buyCountry"
                    value={country}
                    onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))}
                    className="mono w-16"
                    maxLength={2}
                    autoCapitalize="characters"
                    spellCheck={false}
                  />
                </div>
                <div className="field">
                  <Label htmlFor="buyContains">Contains</Label>
                  <Input
                    id="buyContains"
                    value={contains}
                    onChange={(e) => setContains(e.target.value)}
                    className="mono w-44"
                    placeholder="digits, or * as wildcard"
                    spellCheck={false}
                  />
                </div>
                <div className="field">
                  <Label htmlFor="buyType">Type</Label>
                  <Select
                    value={numType}
                    onValueChange={(v) => setNumType(v as 'local' | 'tollfree')}
                  >
                    <SelectTrigger id="buyType" className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="local">local</SelectItem>
                      <SelectItem value="tollfree">toll-free</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  size="sm"
                  className="h-8"
                  onClick={runSearch}
                  disabled={searching}
                >
                  {searching ? 'Searching…' : 'Search'}
                </Button>
              </div>
              {searchError && <p className="err text-sm">{searchError}</p>}
              {buyError && <p className="err text-sm">{buyError}</p>}
              {results !== null && (
                <DataTable
                  columns={availColumns}
                  rows={results}
                  keyOf={(r) => r.number}
                  fixed
                  maxHeight="22rem"
                  emptyTitle="No numbers offered"
                  emptyBody={
                    <>
                      Twilio offered nothing for that search — loosen the contains filter, or
                      try the other number type.
                    </>
                  }
                />
              )}
            </>
          )}
        </div>
      </section>
    </div>
  )
}
